/* aviaf — shared UI primitives + plot widgets */
const { useState, useEffect, useMemo, useRef } = React;

/* ── Skeleton / shimmer loading ───────────────────────────── */
window.Skel = function Skel({ kind, w, h, style, className = "", ...rest }) {
  const cls = "skel" + (kind ? " skel-" + kind : "") + (className ? " " + className : "");
  const s = { ...(style || {}) };
  if (w !== undefined) s.width = typeof w === "number" ? w + "px" : w;
  if (h !== undefined) s.height = typeof h === "number" ? h + "px" : h;
  return <span className={cls} style={s} {...rest} />;
};

window.SkelText = function SkelText({ lines = 3, widths }) {
  const ws = widths || ["92%", "78%", "60%"];
  return (
    <span className="skel-stack" style={{display:"flex",flexDirection:"column",gap:6}}>
      {Array.from({length: lines}).map((_, i) => (
        <window.Skel key={i} kind="line" w={ws[i % ws.length]} />
      ))}
    </span>
  );
};

/* Global "fresh load" state — every page mount briefly shows skeletons.
   Bumping the reloadKey re-triggers it. */
window.__reloadKey = 0;
window.__reloadSubs = new Set();
window.triggerReload = function (ms = 1600) {
  window.__reloadKey++;
  window.__reloadLastMs = ms;
  window.__reloadSubs.forEach(fn => fn(window.__reloadKey, ms));
};

window.useDataLoad = function useDataLoad(ms = 1400) {
  // Purely data-driven: returns true (= show shimmer) whenever
  // window.__LIVE.hasData is false.  Re-renders on live-data bumps via
  // useSyncExternalStore (window.useLive) instead of polling __LIVE.rev
  // every 250ms — hasData flips through markLoaded()→bump(), so the
  // shimmer dissolves the instant the source resolves, with zero idle
  // timers.  triggerReload(ms) still forces loading=true for `ms`; a
  // single timeout (not a poll) clears it when the window elapses.
  window.useLive();
  const [forcedUntil, setForcedUntil] = useState(0);
  const [, force] = useState(0);
  useEffect(() => {
    const fn = (_k, ms2) => setForcedUntil(Date.now() + (ms2 || ms));
    window.__reloadSubs.add(fn);
    return () => window.__reloadSubs.delete(fn);
  }, [ms]);
  useEffect(() => {
    const left = forcedUntil - Date.now();
    if (left <= 0) return;
    const id = setTimeout(() => force(x => x + 1), left);
    return () => clearTimeout(id);
  }, [forcedUntil]);
  if (Date.now() < forcedUntil) return true;
  return !window.__LIVE?.hasData;
};

/* ── ErrorBoundary ─────────────────────────────────────────
 * Wrap UI subtrees that read shape-flexible data so a single bad
 * field doesn't unmount the entire page.  Renders a small inline
 * message + posts to /api/client-errors so the hub log captures the
 * stack.  Use generously around panels that read live data shapes
 * (SpcPanel, HealthPanel, AccountCard, StratMiniTable). */
class _ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    try {
      fetch("/api/client-errors", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          kind: "react",
          message: String(err?.message || err),
          stack: String(err?.stack || "").slice(0, 8000),
          component_stack: String(info?.componentStack || "").slice(0, 8000),
          url: location.href,
          ua: navigator.userAgent.slice(0, 500),
        }),
      }).catch(() => {});
    } catch (_) { /* never let reporter throw inside boundary */ }
  }
  render() {
    if (!this.state.err) return this.props.children;
    const label = this.props.label || "section";
    return (
      <div style={{
        padding: "12px var(--pad)", fontFamily: "var(--mono)", fontSize: 11,
        color: "var(--neg)", borderTop: "1px solid var(--rule)",
        borderBottom: "1px solid var(--rule)", background: "var(--paper-2)",
      }}>
        ⚠ {label} failed to render — {String(this.state.err?.message || this.state.err).slice(0, 200)}
      </div>
    );
  }
}
window.ErrorBoundary = _ErrorBoundary;

window.Sparkline = function Sparkline({ data, width = 220, height = 26, stroke = "currentColor", fill = false }) {
  // Guard against empty / 1-point arrays (data.length-1 = 0 → step = Infinity
  // → i*step = NaN → path d="MNaN …" which React errors on at render time
  // and tears down the parent subtree).  Also filter non-finite values that
  // would NaN-poison the geometry.
  const safe = (data || []).filter(v => Number.isFinite(v));
  if (safe.length < 2) {
    return (
      <svg className="spark" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <line x1={0} y1={height / 2} x2={width} y2={height / 2}
              stroke={stroke} strokeWidth="1" strokeDasharray="2 3" opacity="0.4"
              vectorEffect="non-scaling-stroke" />
      </svg>);
  }
  const min = Math.min(...safe), max = Math.max(...safe);
  const range = (max - min) || 1;
  const step = width / (safe.length - 1);
  const pts = safe.map((v, i) => [i * step, height - ((v - min) / range) * height]);
  const d = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  return (
    <svg className="spark" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {fill && <path d={d + ` L ${width} ${height} L 0 ${height} Z`} fill="currentColor" opacity="0.08" />}
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.25" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

window.StateBadge = function StateBadge({ state, size = "sm" }) {
  const m = {
    running: { l: "RUN", c: "var(--pos)" },
    armed:   { l: "ARM", c: "var(--accent)" },
    paused:  { l: "PSE", c: "var(--warn)" },
    stopped: { l: "OFF", c: "var(--muted)" },
    breached:{ l: "BRC", c: "var(--neg)" },
    degraded:{ l: "DEG", c: "var(--warn)" },
  }[state] || { l: state.toUpperCase().slice(0,3), c: "var(--muted)" };
  return (
    <span style={{
      display: "inline-block", padding: size === "sm" ? "1px 6px" : "2px 8px",
      border: `1px solid ${m.c}`, color: m.c,
      fontSize: size === "sm" ? 10 : 11, letterSpacing: "0.06em",
      fontFamily: "var(--mono)",
    }}>{m.l}</span>
  );
};

window.SectionHeader = function SectionHeader({ title, count, right, sub }) {
  return (
    <div className="section-h">
      <h2>
        {title}
        {count !== undefined && <span className="count tnum">{count}</span>}
        {sub && <span className="count tnum mono muted" style={{fontSize:11, fontWeight:400}}>{sub}</span>}
      </h2>
      {right && <div className="section-tools">{right}</div>}
    </div>
  );
};

window.PageHeader = function PageHeader({ crumbs, actions }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "16px var(--pad)", borderBottom: "1px solid var(--rule)",
    }}>
      <div className="mono" style={{ fontSize: 13 }}>
        {crumbs.map((c, i) => (
          <span key={i}>
            {i > 0 && <span style={{ color: "var(--muted-2)", margin: "0 8px" }}>/</span>}
            {c.href
              ? <a href={c.href} style={{ color: "var(--muted)", textDecoration: "none" }}>{c.label}</a>
              : <span style={{ color: "var(--ink)", fontWeight: 500 }}>{c.label}</span>}
          </span>
        ))}
      </div>
      {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
    </div>
  );
};

window.Btn = function Btn({ children, onClick, kind = "ghost", title, disabled = false }) {
  const styles = {
    ghost:  { bg: "transparent", c: "var(--ink)",   bd: "var(--rule-2)" },
    primary:{ bg: "var(--ink)",  c: "var(--paper)", bd: "var(--ink)" },
    danger: { bg: "transparent", c: "var(--neg)",   bd: "var(--neg)" },
    warn:   { bg: "transparent", c: "var(--warn)",  bd: "var(--warn)" },
    accent: { bg: "transparent", c: "var(--accent)",bd: "var(--accent)" },
  }[kind] || {};
  return (
    <button onClick={disabled ? undefined : onClick} title={title} disabled={disabled} style={{
      background: styles.bg, color: styles.c, border: `1px solid ${styles.bd}`,
      fontFamily: "var(--mono)", fontSize: 11, padding: "5px 10px",
      letterSpacing: "0.04em", cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.45 : 1, borderRadius: 0,
    }}>{children}</button>
  );
};

/* equity / drawdown / daily plot — time-window aware */
window.EquityPlot = function EquityPlot({ showGrid, series: seriesIn, height = 220, range = "30D", setRange, tabs = true, defaultTab = "pnl" }) {
  const [tab, setTab] = useState(defaultTab);
  const [internalRange, setInternalRange] = useState(range);
  const r = setRange ? range : internalRange;
  const setR = setRange || setInternalRange;
  const ytdDays = Math.max(
    1,
    Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86_400_000),
  );
  const rangeDays = { "5D": 5, "30D": 30, "90D": 90, "YTD": ytdDays }[r] || 30;
  const windowUs = rangeDays * 86_400 * 1_000_000;
  const nowUs = Date.now() * 1000;
  const windowStartUs = nowUs - windowUs;

  // Slice both data and ts at the same window boundary so they stay
  // index-aligned.  Returns {data, ts}; ts may be null if no usable ts
  // input (in which case the caller falls back to an index-based tail).
  const filterByWindow = (values, ts) => {
    if (!Array.isArray(ts) || ts.length !== values.length) {
      return {
        data: values.slice(-Math.max(10, Math.round(rangeDays * 8))),
        ts: null,
      };
    }
    const start = ts.findIndex(t => (nowUs - t) <= windowUs);
    if (start < 0) return { data: values.slice(-1), ts: ts.slice(-1) };
    return { data: values.slice(start), ts: ts.slice(start) };
  };

  const STRAT_COLORS = ["var(--accent)", "var(--warn)", "var(--pos)", "var(--neg)", "var(--muted)"];
  const prettyStrat = (s) =>
    (typeof window.prettyStrategy === "function") ? window.prettyStrategy(s) :
    s ? s.replace(/_/g, " ") : "—";
  const anchorToWindow = (xs) => {
    if (!xs || xs.length === 0) return xs;
    const base = xs[0] || 0;
    return xs.map(v => (v == null ? v : v - base));
  };

  // Track __LIVE.rev so useMemo refires when data-live mutates window.*
  // (EQUITY_TOTAL / PNL_BY_STRATEGY / EQUITY_TS).  Without this dep the
  // memo locks in whichever state was present on first render — making
  // the plot show stale data while sibling KPI tiles read fresh state.
  const liveRev = window.__LIVE?.rev || 0;
  const series = useMemo(() => {
    if (seriesIn) {
      const trim = (s) => {
        const { data, ts } = filterByWindow(s.data || [], s.ts);
        return { ...s, data, ts };
      };
      const base = seriesIn;
      if (tab === "drawdown") {
        const dd = (xs) => { let p=-Infinity; return xs.map(v=>{p=Math.max(p,v);return v-p;}); };
        const src = trim(base[0] || { data: [] });
        return [{ k: "dd", lbl: "DRAWDOWN", color: "var(--neg)", data: dd(src.data), ts: src.ts }];
      }
      if (tab === "daily") {
        const src = trim(base[0] || { data: [] });
        const d = src.data.map((v,i,a)=>i?v-a[i-1]:0);
        return [{ k: "daily", lbl: "DAILY P&L", color: "var(--ink)", data: d, bars: true, ts: src.ts }];
      }
      return base.map(trim);
    }

    const eqTotal = window.EQUITY_TOTAL || [];
    const pnlTotal = window.PNL_TOTAL || [];
    const ts = window.EQUITY_TS || [];

    if (tab === "aum") {
      const eq = filterByWindow(eqTotal, ts);
      return [{ k: "aum", lbl: "AUM", color: "var(--ink)", data: eq.data, ts: eq.ts, emphasis: true }];
    }
    if (tab === "pnl") {
      const pbs = window.PNL_BY_STRATEGY || {};
      const slugs = Object.keys(pbs).filter(k => (pbs[k] || []).length > 0);
      const rangeLbl = ({ "5D": "5D P&L", "30D": "30D P&L", "90D": "90D P&L", "YTD": "YTD P&L" })[r] || "P&L";
      const stratLines = slugs.map((slug, i) => {
        const f = filterByWindow(pbs[slug] || [], ts);
        return {
          k: `pnl-${slug}`,
          lbl: prettyStrat(slug).toUpperCase(),
          color: STRAT_COLORS[i % STRAT_COLORS.length],
          data: anchorToWindow(f.data),
          ts: f.ts,
        };
      });
      const fo = filterByWindow(pnlTotal.length ? pnlTotal : eqTotal, ts);
      const overall = {
        k: "overall_pnl", lbl: rangeLbl, color: "var(--ink)",
        data: anchorToWindow(fo.data),
        ts: fo.ts,
        emphasis: true,
      };
      return stratLines.length ? [overall, ...stratLines] : [overall];
    }
    if (tab === "drawdown") {
      const f = filterByWindow(eqTotal, ts);
      const dd = (xs) => { let p=-Infinity; return xs.map(v=>{p=Math.max(p,v);return v-p;}); };
      return [{ k: "dd", lbl: "DRAWDOWN", color: "var(--neg)", data: dd(f.data), ts: f.ts }];
    }
    if (tab === "daily") {
      const f = filterByWindow(eqTotal, ts);
      const d = f.data.map((v,i,a)=>i?v-a[i-1]:0);
      return [{ k: "daily", lbl: "DAILY P&L", color: "var(--ink)", data: d, bars: true, ts: f.ts }];
    }
  }, [tab, r, seriesIn, rangeDays, liveRev]);

  const w = 1000, h = height, padL = 56, padR = 12, padT = 12, padB = 28;
  // Guard against undefined series (memo's switch may not match) and
  // empty data arrays so an empty-state account doesn't crash the plot.
  const safeSeries = (series && series.length) ? series : [{ k:"empty", lbl:"—", color:"var(--muted)", data:[] }];
  const all = safeSeries.flatMap(s => (s.data || [])).filter(v => Number.isFinite(v));
  let lo = all.length ? Math.min(...all) : 0;
  let hi = all.length ? Math.max(...all) : 1;
  if (lo === hi) {
    const pad = Math.abs(lo) * 0.05 || 1;
    lo -= pad; hi += pad;
  }
  const span = hi - lo;
  lo -= span * 0.06; hi += span * 0.06;
  const ySpan = (hi - lo) || 1;
  // Time-based x mapping: the plot always spans the SELECTED window
  // (windowStartUs → nowUs), so the 5D / 30D / 90D / YTD buttons change
  // the x-axis scale itself, not just slice the data underneath.  Sparse
  // history shows as a line clustered at the right edge with empty space
  // on the left — making it obvious that the longer window doesn't have
  // history yet, instead of stretching whatever data exists across the
  // full plot width and visually masking the difference between ranges.
  const xByTs = (ts) => padL + ((ts - windowStartUs) / windowUs) * (w - padL - padR);
  // Index-based fallback for series with no usable ts (rare: legacy
  // pages or test fixtures that don't carry timestamps).
  const xByIx = (i, n) => padL + (n > 1 ? (i / (n - 1)) : 0.5) * (w - padL - padR);
  const xAt = (s, i) => (Array.isArray(s.ts) && s.ts.length === s.data.length)
    ? xByTs(s.ts[i])
    : xByIx(i, s.data.length);
  const y = (v) => padT + (1 - ((Number.isFinite(v) ? v : lo) - lo) / ySpan) * (h - padT - padB);
  const yTicks = 4;
  const ticks = Array.from({ length: yTicks + 1 }, (_, i) => lo + (hi - lo) * (i / yTicks));
  const last = safeSeries.map(s => (s.data && s.data.length) ? s.data.at(-1) : null);

  return (
    <div className="plot-wrap">
      {tabs && (
        <div className="plot-tabs">
          {[["aum","AUM"],["pnl","P&L"],["drawdown","Drawdown"],["daily","Daily P&L"]].map(([k,l]) => (
            <div key={k} className="plot-tab" data-active={tab===k} onClick={()=>setTab(k)}>{l}</div>
          ))}
          <div style={{flex:1}} />
          {["5D","30D","90D","YTD"].map(rr => (
            <div key={rr} className="plot-tab" data-active={r===rr} onClick={()=>setR(rr)}>{rr}</div>
          ))}
        </div>
      )}
      <div className="plot-legend">
        {safeSeries.map((s, i) => (
          <span key={s.k} style={{ color: s.color }}>
            <span className="swatch" style={{ background: "currentColor" }} />
            <span style={{ color: "var(--ink-2)" }}>{s.lbl}</span>{" "}
            <span className="muted tnum">{window.fmt$(last[i], 0)}</span>
          </span>
        ))}
      </div>
      <svg className="plot-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        {ticks.map((t, i) => (
          <g key={i}>
            {showGrid && <line x1={padL} x2={w-padR} y1={y(t)} y2={y(t)} stroke="var(--rule)" strokeWidth="1" vectorEffect="non-scaling-stroke" />}
            <text x={padL-8} y={y(t)+3} textAnchor="end" fontFamily="var(--mono)" fontSize="10" fill="var(--muted)">{window.fmt$(t,0)}</text>
          </g>
        ))}
        {(() => {
          // X-axis labels span the SELECTED window (windowStartUs → nowUs)
          // regardless of how much data is loaded.  This is what makes
          // the 5D / 30D / 90D / YTD buttons visibly change the scale:
          // 30D shows a full 30-day axis, with the actual data line
          // sitting in whatever portion of that axis has history.
          const spanMs = windowUs / 1000;
          const fmt = (d) =>
            spanMs < 36 * 3600 * 1000
              ? d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})
              : spanMs < 5 * 86_400_000
                ? d.toLocaleString("en-US",{month:"short",day:"numeric",hour:"numeric"})
                : d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
          return Array.from({length:6},(_,i)=>{
            const t = i/5;
            const tsUs = windowStartUs + t * windowUs;
            const d = new Date(tsUs/1000);
            const xPos = padL + t * (w - padL - padR);
            return <text key={i} x={xPos} y={h-8} textAnchor="middle" fontFamily="var(--mono)" fontSize="10" fill="var(--muted)">{fmt(d)}</text>;
          });
        })()}
        {lo<0&&hi>0&&<line x1={padL} x2={w-padR} y1={y(0)} y2={y(0)} stroke="var(--ink)" strokeWidth="1" strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />}
        {safeSeries.map(s=>{
          if (!s.data || s.data.length === 0) return null;
          if(s.bars){
            // Bar width: window-width divided by the densest expected
            // bucket cadence (hourly for aggregate, daily for some
            // detail-page series).  Approximate by spacing between
            // consecutive ts when available; fall back to even spacing.
            const tsArr = (Array.isArray(s.ts) && s.ts.length === s.data.length) ? s.ts : null;
            const dt = tsArr && tsArr.length > 1
              ? (tsArr[tsArr.length-1] - tsArr[0]) / Math.max(1, tsArr.length - 1)
              : windowUs / Math.max(1, s.data.length);
            const bw = Math.max(1, (dt / windowUs) * (w - padL - padR) * 0.7);
            return <g key={s.k}>{s.data.map((v,i)=>(
              <rect key={i} x={xAt(s,i)-bw/2} y={Math.min(y(v),y(0))} width={bw} height={Math.abs(y(v)-y(0))} fill={v>=0?"var(--pos)":"var(--neg)"} />
            ))}</g>;
          }
          const d=s.data.map((v,i)=>(i?"L":"M")+xAt(s,i).toFixed(1)+" "+y(v).toFixed(1)).join(" ");
          const emph = s.emphasis || s.k === "total" || s.k === "overall_pnl" || s.k === "aum";
          const xFirst = xAt(s, 0);
          const xLast = xAt(s, s.data.length - 1);
          return <g key={s.k} style={{color:s.color}}>
            {emph && tab==="aum" && <path d={d+` L ${xLast.toFixed(1)} ${h-padB} L ${xFirst.toFixed(1)} ${h-padB} Z`} fill="currentColor" opacity="0.06" />}
            <path d={d} fill="none" stroke="currentColor" strokeWidth={emph?1.8:1.1} vectorEffect="non-scaling-stroke" />
            <circle cx={xLast} cy={y(s.data.at(-1))} r="2.5" fill="currentColor" />
          </g>;
        })}
      </svg>
    </div>
  );
};

/* ── Toasts + confirm modal ──────────────────────────────────
 * Site-native replacements for browser confirm() and the tiny inline
 * command status text:
 *   window.pushToast({kind,title,msg,ttl})      → bottom-right stack
 *   await window.confirmModal({title,body,...})  → Promise<boolean>
 * Mounted once at the app root via <window.ToastHost/> + <window.ConfirmHost/>. */
const _toastStore = { items: [], seq: 0, subs: new Set() };
function _toastNotify() { _toastStore.subs.forEach((fn) => { try { fn(); } catch (_) {} }); }
window.pushToast = function pushToast(opts) {
  const o = typeof opts === "string" ? { msg: opts } : (opts || {});
  const id = ++_toastStore.seq;
  const kind = o.kind || "info";
  _toastStore.items = [..._toastStore.items, { id, kind, title: o.title || null, msg: o.msg || "" }];
  _toastNotify();
  const ttl = o.ttl != null ? o.ttl : (kind === "error" ? 12000 : kind === "warn" ? 8000 : 5000);
  if (ttl > 0) setTimeout(() => window.dismissToast(id), ttl);
  return id;
};
window.dismissToast = function dismissToast(id) {
  _toastStore.items = _toastStore.items.filter((t) => t.id !== id);
  _toastNotify();
};
window.ToastHost = function ToastHost() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => (n + 1) | 0);
    _toastStore.subs.add(fn);
    return () => _toastStore.subs.delete(fn);
  }, []);
  const items = _toastStore.items;
  if (!items.length) return null;
  const icon = { info: "ℹ", success: "✓", warn: "▲", error: "✕" };
  return (
    <div className="toast-host">
      {items.map((t) => (
        <div key={t.id} className="toast" data-kind={t.kind} role="status">
          <span className="toast-i" aria-hidden="true">{icon[t.kind] || "•"}</span>
          <div className="toast-body">
            {t.title && <div className="toast-t">{t.title}</div>}
            {t.msg && <div className="toast-m">{t.msg}</div>}
          </div>
          <button className="toast-x" onClick={() => window.dismissToast(t.id)} aria-label="dismiss">×</button>
        </div>
      ))}
    </div>
  );
};

const _confirmStore = { current: null, subs: new Set() };
function _confirmNotify() { _confirmStore.subs.forEach((fn) => { try { fn(); } catch (_) {} }); }
window.confirmModal = function confirmModal(opts) {
  const o = typeof opts === "string" ? { body: opts } : (opts || {});
  return new Promise((resolve) => {
    _confirmStore.current = {
      title: o.title || "Confirm",
      body: o.body || "",
      confirmLabel: o.confirmLabel || "Confirm",
      cancelLabel: o.cancelLabel || "Cancel",
      danger: !!o.danger,
      resolve,
    };
    _confirmNotify();
  });
};
window.ConfirmHost = function ConfirmHost() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => (n + 1) | 0);
    _confirmStore.subs.add(fn);
    return () => _confirmStore.subs.delete(fn);
  }, []);
  const c = _confirmStore.current;
  const close = (val) => {
    if (!_confirmStore.current) return;
    const r = _confirmStore.current.resolve;
    _confirmStore.current = null;
    _confirmNotify();
    if (r) r(val);
  };
  useEffect(() => {
    if (!c) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      else if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [c]);
  if (!c) return null;
  return (
    <div className="modal-scrim" onClick={() => close(false)}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={"modal-h" + (c.danger ? " danger" : "")}>{c.title}</div>
        <div className="modal-b">{c.body}</div>
        <div className="modal-f">
          <window.Btn onClick={() => close(false)}>{c.cancelLabel}</window.Btn>
          <window.Btn kind={c.danger ? "danger" : "primary"} onClick={() => close(true)}>{c.confirmLabel}</window.Btn>
        </div>
      </div>
    </div>
  );
};
